import { colors, Radio, RadioGroup } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { AlertPopup } from "components/alerts/popups/AlertPopup";
import useIsMount from "hooks/useIsMount";
import { set } from "lodash";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
    AUTOCOMPLETE_API_MIN_CHARS,
    OTDA_GDPR_MESSAGE,
} from "../../../constants/generalConstants";
import useThrottle from "../../../hooks/useThrottle";
import { setShouldEnableAddressEditing } from "../../../redux/actions/createRequisitionActions";
import {
    createOneTimeDeliveryAddressPost,
    deliveryLocAutocompleteGet,
    deliveryOneTimeDeliveryAddressAutocompleteGet,
} from "../../../services/requisition/requisitionAPIs";
import { freeTextFormProps } from "../../../styled/props/textFieldProps";
import ValidatedAutocomplete from "../validatedAutoComplete";
import {
    DELIVERY_LOC_INPUT_LABEL,
    DELIVERY_LOC_INPUT_PLACEHOLDER,
    DELIVERY_ONE_TIME_ADDRESS_INPUT,
    MIN_3_CHARACTERS_TEXT,
    NO_OPTIONS_TEXT,
    START_NEW_SEARCH_TEXT,
} from "./labels";

const DeliveryLocSearchAsYouType = ({
    selectedDeliveryLoc,
    hasAddressType,
    onSelectDeliveryLoc,
    disabled,
    onSwitchMultiDeliveryOption,
    shouldShowOTA,
    enableAddressPropEditing = false,
}) => {
    // Collect states and state dispatchers
    const isPostPoReq = useSelector(
        (state) => state.createRequisition.isPostPoReq
    );

    const [addressTypeSelected, setAddressTypeSelected] = useState(
        selectedDeliveryLoc?.ADDRESS_TYPE || hasAddressType || "standard"
    );
    const [selectedOTDACountry, setSelectedOTDACountry] = useState(
        selectedDeliveryLoc?.ONE_TIME_ADDRESS_COUNTRY ?? ""
    );
    const [OTDAddressValue, setOTDAddressValue] = useState(
        selectedDeliveryLoc?.ONE_TIME_ADDRESS ?? ""
    );
    const [newOTDAInputErrors, setOTDAInputErrors] = useState("");
    const [saveAddressForFuture, setSaveAddressForFuture] = useState(
        selectedDeliveryLoc?.SAVE_ADDRESS_FOR_FUTURE ?? false
    );
    const [showGDPRMessage, setShowGDPRMessage] = useState(false);
    const [hasAgreedOTAGDPRNotice, setHasAgreedOTAGDPRNotice] = useState(false);
    const [notReadyToSave, setNotReadyToSave] = useState(true);

    // Local states
    const [noOptionsText, setNoOptionsText] = useState(
        selectedDeliveryLoc?.LOCATION_CODE
            ? START_NEW_SEARCH_TEXT
            : MIN_3_CHARACTERS_TEXT
    );
    const [deliveryLocLoading, setDeliveryLocLoading] = useState(false);
    const [deliveryLocOptions, setDeliveryLocOptions] = useState([]);
    const [deliveryLocSearchTerm, setDeliveryLocSearchTerm] = useState(
        selectedDeliveryLoc?.LOCATION_CODE || ""
    );

    // Instantiate hooks required for search as you type
    const dispatch = useDispatch();
    const throttleFunction = useThrottle();
    const isFirstRender = useIsMount();

    useEffect(() => {
        if (!enableAddressPropEditing) {
            // check if address can be edited
            dispatch(
                setShouldEnableAddressEditing(
                    addressTypeSelected === "standard" ? true : false
                )
            );
        }
    }, [addressTypeSelected]);

    useEffect(() => {
        // If no input, provided, or less than 3 characters input, API not called
        if (!isFirstRender) {
            throttleFunction(() => {
                if (
                    deliveryLocSearchTerm === "" ||
                    deliveryLocSearchTerm.length < AUTOCOMPLETE_API_MIN_CHARS
                ) {
                    setDeliveryLocOptions([]);
                    setNoOptionsText(MIN_3_CHARACTERS_TEXT);
                    return;
                }

                // Else, call get location API
                selectedDeliveryLoc
                    ? setNoOptionsText(START_NEW_SEARCH_TEXT)
                    : setNoOptionsText(NO_OPTIONS_TEXT);

                if (addressTypeSelected === "standard") {
                    deliveryLocAutocompleteGet(
                        dispatch,
                        deliveryLocSearchTerm,
                        setDeliveryLocOptions,
                        selectedDeliveryLoc,
                        setDeliveryLocLoading
                    );
                } else if (addressTypeSelected === "otda") {
                    if (!selectedDeliveryLoc?.SELECTED_ONE_TIME_ADDRESS) {
                        deliveryOneTimeDeliveryAddressAutocompleteGet(
                            dispatch,
                            deliveryLocSearchTerm,
                            setDeliveryLocOptions,
                            selectedDeliveryLoc,
                            setDeliveryLocLoading
                        );
                    }
                }
            });
        }
    }, [deliveryLocSearchTerm]);

    // Helper functions for the MUI autocomplete
    const onTypeSearchTerm = (_event, newInputValue) => {
        const oldInputValue = deliveryLocSearchTerm;
        setDeliveryLocSearchTerm(newInputValue);
        if (
            selectedOTDACountry !== "" &&
            newInputValue !== "" &&
            oldInputValue.length < newInputValue.length
        )
            setOTDAInputErrors("Clear Country to search Location");
        else if (
            OTDAddressValue !== "" &&
            newInputValue !== "" &&
            oldInputValue.length < newInputValue.length
        )
            setOTDAInputErrors(
                "Clear One Time Delivery Address to search Location"
            );
        else setOTDAInputErrors("");
    };

    const getOptionLabel = (option) => {
        if (
            addressTypeSelected === "standard" ||
            hasAddressType === "standard"
        ) {
            return option.LOCATION_CODE;
        } else if (
            addressTypeSelected === "otda" ||
            hasAddressType === "otda"
        ) {
            return option?.ADDRESS != undefined ? option.ADDRESS : "";
        }
    };

    const onClickDeliveryLoc = (e, value) => {
        /* Called when a delivery location is selected by a user, writing the selection
        to the create requisition application state.

        Parameters
        ----------
        value : obj
            The obj returned from the `onChange` method on the
            autocomplete {"LOCATION_ID": 1001, "LOCATION_CODE": "Location Code"}
         */

        //set this property to undefined as it is used as condition for calling the autocomplete list
        if (addressTypeSelected === "otda") {
            selectedDeliveryLoc.SELECTED_ONE_TIME_ADDRESS = "";
        }

        let locationObj = {};
        locationObj.ADDRESS_TYPE = addressTypeSelected;
        if (addressTypeSelected === "standard") {
            locationObj.LOCATION_ID = value?.LOCATION_ID;
            locationObj.LOCATION_CODE = value?.LOCATION_CODE;
        } else if (addressTypeSelected === "otda") {
            locationObj.SELECTED_ONE_TIME_ADDRESS =
                value?.ADDRESS != undefined ? value?.ADDRESS : null;
        }

        onSelectDeliveryLoc(locationObj);
    };
    const checkIfSelectedValueIsAnOption = (option, value) => {
        /* Called as part of autocomplete `isOptionEqualToValue` prop.

        Parameters
        ----------
        option : obj
            The structure of the autocomplete options.
        value : obj
            The obj returned from the `onChange` method on the
            autocomplete 

        Notes
        ----
        Both `option` and `value` are structured the same:
            {"LOCATION_ID": 1001, "LOCATION_CODE": "Location Code"}
         */

        return option.id == value.id;
    };

    const handleCheckboxClick = (e) => {
        if (selectedOTDACountry !== "" && OTDAddressValue !== "") {
            setShowGDPRMessage(e.target.checked);
            setSaveAddressForFuture(e.target.checked);
            onSelectDeliveryLoc({
                ONE_TIME_ADDRESS: OTDAddressValue,
                ONE_TIME_ADDRESS_COUNTRY: selectedOTDACountry,
                ADDRESS_TYPE: "otda",
                SAVE_ADDRESS_FOR_FUTURE: e.target.checked,
            });

            if (e.target.checked == true) {
                createOneTimeDeliveryAddressPost(
                    dispatch,
                    OTDAddressValue,
                    selectedOTDACountry
                );
            }
        } else {
            setShowGDPRMessage(false);
            setSaveAddressForFuture(false);
            onSelectDeliveryLoc({
                ONE_TIME_ADDRESS: OTDAddressValue,
                ONE_TIME_ADDRESS_COUNTRY: selectedOTDACountry,
                ADDRESS_TYPE: "otda",
                SAVE_ADDRESS_FOR_FUTURE: false,
            });
        }

        // show error
    };

    const changeAddressType = (value) => {
        if (value != addressTypeSelected) {
            // if you change address type, wipe the data
            onSwitchMultiDeliveryOption(value);
            setDeliveryLocSearchTerm("");
        }
        // set local state
        if (value === "otda") {
            setAddressTypeSelected("otda");
        } else if (value === "standard") {
            setAddressTypeSelected("standard");
            setOTDAInputErrors("");
            setOTDAddressValue("");
            setSelectedOTDACountry("");
            setSaveAddressForFuture(false);
            setShowGDPRMessage(false);
        }
    };

    const onTypeAddress = (value) => {
        const oldInputValue = OTDAddressValue;
        if (value.length > 0) {
            setNotReadyToSave(false);
        } else {
            setNotReadyToSave(true);
        }

        setOTDAddressValue(value);

        onSelectDeliveryLoc({
            ONE_TIME_ADDRESS: value,
            ONE_TIME_ADDRESS_COUNTRY: selectedOTDACountry,
            ADDRESS_TYPE: "otda",
            SAVE_ADDRESS_FOR_FUTURE: saveAddressForFuture,
        });
        if (
            (selectedDeliveryLoc?.LOCATION_CODE !== undefined ||
                selectedDeliveryLoc?.LOCATION_CODE !== "") &&
            deliveryLocSearchTerm !== "" &&
            value !== "" &&
            oldInputValue.length < value.length
        ) {
            setOTDAInputErrors(
                "Clear location to enter One Time Delivey Address"
            );
        } else {
            setOTDAInputErrors("");
        }
    };

    const onSelectCountry = (value) => {
        const oldInputValue = selectedOTDACountry;
        setSelectedOTDACountry(value);
        onSelectDeliveryLoc({
            ONE_TIME_ADDRESS: OTDAddressValue,
            ONE_TIME_ADDRESS_COUNTRY: value,
            ADDRESS_TYPE: "otda",
            SAVE_ADDRESS_FOR_FUTURE: saveAddressForFuture,
        });
        if (
            (selectedDeliveryLoc?.LOCATION_CODE !== undefined ||
                selectedDeliveryLoc?.LOCATION_CODE !== "") &&
            deliveryLocSearchTerm !== "" &&
            value !== "" &&
            oldInputValue.length < value.length
        ) {
            setOTDAInputErrors("Clear location to select country");
        } else {
            setOTDAInputErrors("");
        }
    };

    return (
        <>
            <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                name="radio-buttons-group"
                onChange={(e) => changeAddressType(e.target.value)}
                aria-disabled={disabled}
            >
                <FormControlLabel
                    value="standard"
                    disabled={disabled}
                    control={<Radio color="secondary" />}
                    label="Look up SSE locations"
                    checked={addressTypeSelected != "otda"}
                />
                {shouldShowOTA && (
                    <FormControlLabel
                        value="otda"
                        disabled={disabled}
                        checked={addressTypeSelected == "otda"}
                        control={<Radio color="secondary" />}
                        label="Input one time delivery address"
                    />
                )}
            </RadioGroup>

            <Autocomplete
                freeSolo
                autoComplete
                clearOnBlur
                filterSelectedOptions
                disabled={isPostPoReq || disabled}
                aria-disabled={disabled}
                loading={deliveryLocLoading}
                filterOptions={(x) => x}
                noOptionsText={noOptionsText}
                options={deliveryLocOptions}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={checkIfSelectedValueIsAnOption}
                onChange={onClickDeliveryLoc}
                onInputChange={onTypeSearchTerm}
                value={
                    addressTypeSelected == "otda"
                        ? selectedDeliveryLoc?.SELECTED_ONE_TIME_ADDRESS != null
                            ? {
                                  ADDRESS:
                                      selectedDeliveryLoc.SELECTED_ONE_TIME_ADDRESS,
                              }
                            : null
                        : selectedDeliveryLoc
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        {...freeTextFormProps}
                        label={DELIVERY_LOC_INPUT_LABEL}
                        multiline
                        placeholder={
                            addressTypeSelected == "otda"
                                ? DELIVERY_ONE_TIME_ADDRESS_INPUT
                                : DELIVERY_LOC_INPUT_PLACEHOLDER
                        }
                    />
                )}
            />

            {addressTypeSelected === "otda" && (
                <>
                    <Box display="flex" justifyContent="center">
                        <Typography
                            style={{ padding: "22px 0 0 10px" }}
                            color={colors.grey[400]}
                        >
                            Or add a One time delivery address
                        </Typography>
                    </Box>
                    <Grid item xs={12} style={{ padding: "12px 0" }}>
                        <ValidatedAutocomplete
                            label="Country"
                            placeholder="Select One Time Address Delivery Country"
                            onChange={(e, value) => onSelectCountry(value)}
                            options={[
                                "England",
                                "Scotland / Northern Ireland",
                                "Wales",
                                "Ireland",
                            ]}
                            value={selectedOTDACountry}
                            disabled={isPostPoReq || disabled}
                            required={OTDAddressValue !== ""}
                            onInputChange={(e, value) => onSelectCountry(value)}
                        />
                    </Grid>

                    <Grid item xs={12} style={{ padding: "12px 0 0 0" }}>
                        <TextField
                            {...freeTextFormProps}
                            label="One Time Delivery Address"
                            multiline
                            onChange={(e) => onTypeAddress(e.target.value)}
                            placeholder="Write your address..."
                            value={OTDAddressValue}
                            disabled={isPostPoReq || disabled}
                            inputProps={{ maxLength: 32458 }}
                            required={selectedOTDACountry !== ""}
                            data-testid="one-time-address-textbox"
                        />
                    </Grid>
                    {newOTDAInputErrors && (
                        <Typography color="error">
                            {newOTDAInputErrors}
                        </Typography>
                    )}

                    <Typography
                        style={{
                            color: `${disabled ? "secondaryLight" : "primary"}`,
                        }}
                    >
                        <FormControlLabel
                            label="Save for future"
                            control={
                                <Checkbox
                                    disabled={notReadyToSave}
                                    onChange={handleCheckboxClick}
                                    checked={saveAddressForFuture}
                                    data-testid="save-future-checkbox"
                                />
                            }
                        />
                    </Typography>
                    <AlertPopup
                        title=""
                        shouldDisplay={
                            !hasAgreedOTAGDPRNotice && showGDPRMessage
                        }
                        hasAgreedOrDismissed={setHasAgreedOTAGDPRNotice}
                        level={"info"}
                        displayMessage={OTDA_GDPR_MESSAGE}
                        actionText={"Ok"}
                        dropShadow={true}
                    />
                </>
            )}
        </>
    );
};

DeliveryLocSearchAsYouType.propTypes = {
    onSelectDeliveryLoc: PropTypes.func,
    selectedDeliveryLoc: PropTypes.shape({
        LOCATION_ID: PropTypes.number,
        LOCATION_CODE: PropTypes.string,
        ONE_TIME_ADDRESS: PropTypes.string,
        ONE_TIME_ADDRESS_COUNTRY: PropTypes.string,
        ADDRESS_TYPE: PropTypes.string,
        SAVE_ADDRESS_FOR_FUTURE: PropTypes.bool,
        SELECTED_ONE_TIME_ADDRESS: PropTypes.string,
    }),
};
export default DeliveryLocSearchAsYouType;
